/**
 * Error codes sent by the backend API server.
 */
export enum ProblemCode {
  /**
   * This is the general fallback. Don't throw this directly, either choose
   * a more specific ProblemCode or just throw a normal Error (or let the
   * original error propagate). The express middleware will catch it, and throw
   * this ProblemCode for you.
   */
  InternalServerError = "InternalServerError",

  /**
   * This account does not have access to the required feature.
   * e.g. the account doesn't have the right paid plan to use the feature.
   */
  AccountFeatureAccessDenied = "AccountFeatureAccessDenied",

  /**
   * The user does not have permission (the necessary role) to do the requested
   * action on the account.
   */
  UserAccountPermissionDenied = "UserAccountPermissionDenied",

  /**
   * Authentication of the request failed.
   * e.g. the user is not authenticated (has not logged in), or their access
   * token has expired and needs to be refreshed.
   */
  Unauthorised = "Unauthorised",

  /**
   * The user is known (authenticated), but they don't have permission to do
   * the requested action.
   * e.g. the user is trying to edit a transaction which doesn't belong to the
   * current account.
   */
  Forbidden = "Forbidden",

  BadRequest = "BadRequest",

  /**
   * Could not find a resource which was required for the API call. Use this
   * for DB lookups where a resource was expected but wasn't returned.
   *
   * These can occur due e.g. due concurrent updates to a resource causing it
   * to no longer be available.
   */
  ResourceNotFound = "ResourceNotFound",

  /**
   * The request failed as the resulting event/ transaction would not be valid.
   */
  TransactionInvalid = "TransactionInvalid",

  /**
   * For validation errors in the **request** itself.
   * e.g. a provided value is out of range.
   */
  ValidationFailed = "ValidationFailed",

  FieldRequired = "FieldRequired",
  FileRequired = "FileRequired",

  /**
   * The user is attempting to import a file type which isn't
   * supported in Syla.
   */
  UnsupportedFileType = "UnsupportedFileType",

  RefreshedExpiredOtp = "RefreshedExpiredOtp",

  /**
   * User attempted an email signup, but they took too long and it expired.
   */
  EmailRegistrationExpired = "EmailRegistrationExpired",

  VerifiedUserAlreadyExists = "VerifiedUserAlreadyExists",

  GoogleAuthFailed = "GoogleAuthFailed",
  EmailDisallowed = "EmailDisallowed",

  /**
   * Login/ auth failed as the email provided does not exist.
   */
  UserEmailNotFound = "UserEmailNotFound",

  /**
   * Login/ auth failed as the password was incorrect.
   */
  IncorrectPassword = "IncorrectPassword",

  /**
   * User has no password set, making auth via email and password impossible.
   */
  UserHasNoPassword = "UserHasNoPassword",

  SamePassword = "SamePassword",

  ExpiredLink = "ExpiredLink",

  InviteExpired = "InviteExpired",
  InviteInvalid = "InviteInvalid",
  InvitePasswordRequired = "InvitePasswordRequired",

  CannotDeleteLastAccount = "CannotDeleteLastAccount",

  AlreadyInProgress = "AlreadyInProgress",

  UserAlreadyHasAccess = "UserAlreadyHasAccess",
  UserInviteNotFound = "UserInviteNotFound", // specifically for an account invite via email

  AccountMustHaveOwner = "AccountMustHaveOwner",

  SubscriptionAlreadyExists = "SubscriptionAlreadyExists",

  /**
   * Exceeded the maximum number of rows allowed in a single import attempt.
   */
  ExceededRecordLimit = "ExceededRecordLimit",

  /**
   * The file format was recognised, but it couldn't be imported due to
   * validation or parsing errors.
   */
  FailedFileImport = "FailedFileImport",

  /**
   * The user has attempted to fill out one of the custom file templates, but
   * it doesn't match the expected format.
   */
  InvalidCustomFileTemplate = "InvalidCustomFileTemplate",

  /**
   * The provided API Key, or Address failed validation.
   */
  ValidateKeysFailed = "ValidateKeysFailed",

  /**
   * The resource is currently locked and can't be accessed.
   *
   * This is used to prevent concurrent access to certain resources.
   */
  ResourceLocked = "ResourceLocked",
}
